/* ============================================================
   Asha Books — Bible Recommender Widget Shell
   File: assets/bible-widget.js

   Handles only the floating launcher and panel.
   The quiz UI and scoring engine live in bible-recommender.js
   and are loaded lazily the first time the panel opens.
   ============================================================ */

(function () {
  'use strict';

  // ── Config ───────────────────────────────────────────────────────
  const CFG = {
    // Delay (ms) before the nudge animation draws attention
    nudgeDelay:   8000,
    // How long to wait before showing nudge tooltip again after close
    nudgeCooldown: 60000,
    // Pages where the widget should NOT appear (URL substrings)
    hideOnPaths:  ['/cart', '/checkout', '/account'],
  };

  // ── Don't show on certain pages ──────────────────────────────────
  const path = window.location.pathname;
  if (CFG.hideOnPaths.some(p => path.includes(p))) return;

  // ── Shared icon SVG ─────────────────────────────────────────────
  const BOOK_SVG = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>`;

  // ── Inject HTML ──────────────────────────────────────────────────
  const wrapper = document.createElement('div');
  wrapper.id = 'bw-root';
  wrapper.innerHTML = `
    <!-- Launcher bubble -->
    <button class="bw__launcher" id="bw-launcher" aria-label="Find your perfect Bible" type="button">
      <span class="bw__launcher-icon">${BOOK_SVG}</span>
      <span class="bw__launcher-label">Need help choosing?</span>
      <span class="bw__launcher-close" id="bw-launcher-close" aria-label="Dismiss">×</span>
    </button>

    <!-- Nudge tooltip -->
    <div class="bw__nudge hidden" id="bw-nudge">
      Not sure which Bible to choose?<br>
      <strong>Answer 5 quick questions →</strong>
    </div>

    <!-- Panel -->
    <div class="bw__panel" id="bw-panel" role="dialog" aria-modal="true" aria-label="Bible Guide">
      <div class="bw__panel-header">
        <div class="bw__panel-brand">
          <span class="bw__panel-brand-icon">${BOOK_SVG}</span>
          <div>
            <div class="bw__panel-title">Your Bible Guide</div>
            <div class="bw__panel-subtitle">We'll find your perfect match</div>
          </div>
        </div>
        <button class="bw__panel-close" id="bw-close" type="button" aria-label="Close">×</button>
      </div>
      <div class="bw__panel-body">
        <div id="bible-recommender"></div>
      </div>
    </div>

    <!-- Backdrop (mobile) -->
    <div class="bw__backdrop hidden" id="bw-backdrop"></div>
  `;
  document.body.appendChild(wrapper);

  // ── Element refs ─────────────────────────────────────────────────
  const launcher      = document.getElementById('bw-launcher');
  const launcherClose = document.getElementById('bw-launcher-close');
  const panel         = document.getElementById('bw-panel');
  const backdrop      = document.getElementById('bw-backdrop');
  const closeBtn      = document.getElementById('bw-close');
  const nudge         = document.getElementById('bw-nudge');

  // ── State ────────────────────────────────────────────────────────
  let isOpen         = false;
  let quizLoaded     = false;
  let nudgeTimer     = null;
  let launcherHidden = false;

  // ── Open / close ─────────────────────────────────────────────────
  function openPanel() {
    isOpen = true;
    panel.classList.add('is-open');
    backdrop.classList.remove('hidden');
    launcher.classList.add('is-panel-open');
    nudge.classList.add('hidden');
    clearTimeout(nudgeTimer);
    document.body.style.overflow = '';   // don't lock body scroll

    if (!quizLoaded) {
      loadQuiz();
    }
  }

  function closePanel() {
    isOpen = false;
    panel.classList.remove('is-open');
    backdrop.classList.add('hidden');
    launcher.classList.remove('is-panel-open');

    // Schedule nudge again after cooldown
    nudgeTimer = setTimeout(showNudge, CFG.nudgeCooldown);
  }

  // ── Launcher bubble dismiss (×) ──────────────────────────────────
  // Hides the entire launcher (user doesn't want it on this visit)
  function dismissLauncher(e) {
    e.stopPropagation();
    launcherHidden = true;
    launcher.classList.add('is-hidden');
    nudge.classList.add('hidden');
    clearTimeout(nudgeTimer);
  }

  // ── Nudge ────────────────────────────────────────────────────────
  function showNudge() {
    if (isOpen || launcherHidden) return;
    nudge.classList.remove('hidden');
    // Auto-hide nudge after 6 seconds
    setTimeout(() => nudge.classList.add('hidden'), 6000);
  }

  // ── Lazy-load quiz scripts ───────────────────────────────────────
  // bible-recommender.css is already loaded via the snippet <link>.
  // We only load bible-recommender.js on first open so it doesn't
  // run or fetch data until the user actually opens the widget.
  function loadQuiz() {
    quizLoaded = true;

    if (typeof window.brConfig === 'undefined') {
      console.warn('[Bible Widget] window.brConfig not set — check your snippet.');
    }

    // If bible-recommender.js was already loaded via the snippet, init fires
    // automatically. Nothing else to do.
    // If you prefer truly deferred loading, uncomment the block below:
    /*
    const script = document.createElement('script');
    script.src = window.brConfig?.jsUrl || '/assets/bible-recommender.js';
    document.head.appendChild(script);
    */
  }

  // ── Events ───────────────────────────────────────────────────────
  launcher.addEventListener('click', (e) => {
    // Don't open if they clicked the × dismiss button
    if (e.target === launcherClose) return;
    isOpen ? closePanel() : openPanel();
  });

  launcherClose.addEventListener('click', dismissLauncher);
  closeBtn.addEventListener('click', closePanel);
  backdrop.addEventListener('click', closePanel);

  // Escape key closes
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && isOpen) closePanel();
  });

  // ── Nudge timer ──────────────────────────────────────────────────
  nudgeTimer = setTimeout(showNudge, CFG.nudgeDelay);

})();
